import React from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SocialCard from "examples/Cards/SocialCard";
import Grow from "@mui/material/Grow";

/**
 * YouTubeSocialCard Component
 * @param {Object} props
 * @param {Array} profiles - List of YouTube profiles
 * @param {Boolean} loading - Loading state
 * @param {String} error - Error message
 * @param {Function} formatCount - Function to format numbers
 * @param {Array} selectedProfiles - Array of selected profiles [{ id, platform }]
 * @param {Function} onProfileSelect - Handler for selecting/deselecting a profile
 */

// Custom Styled Tooltip with Left Alignment
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400, // Set a reasonable width
    textAlign: "left", // Ensure left alignment
  },
});

const tooltipContent = (
  <div style={{ maxWidth: "350px", textAlign: "left" }}>
    <Typography variant="body2" gutterBottom>
      <strong>How to Use Social Stash:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Store and manage saved <strong>youtube</strong> profiles. <br />
      - Select profiles to analyse profiles. <br />
      - Selete profiles to delete if they are not needed. <br />
    </Typography>

    <Typography variant="body2" gutterBottom>
      <strong>Quick Actions:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Use the <strong>search bar</strong> to quickly find profiles. <br />
      - Click <strong>Add Profile</strong> to save new profiles. <br />
    </Typography>

    <Typography variant="body2" gutterBottom>
      <strong>Generating Reports:</strong>
    </Typography>
    <Typography variant="caption" display="block">
      - Select one or more profiles. <br />
      - Click the <strong>Analyse Profile</strong> button. <br />
      - Cross platform reporting is also available. <br />
    </Typography>
  </div>
);

const YouTubeSocialCard = ({
  profiles,
  loading = false,
  error = null,
  formatCount,
  selectedProfiles,
  onProfileSelect,
}) => {
  // Check if a profile is selected
  const isSelected = (profileId) => {
    return selectedProfiles.some(
      (selected) => selected.id === profileId && selected.platform === "YouTube"
    );
  };

  return (
    <Card>
      {/* Header Section */}
      <MDBox>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          flexDirection="row"
        >
          <MDTypography variant="h6" color="white">
            YouTube
          </MDTypography>
          <CustomTooltip
            title={tooltipContent}
            placement="bottom"
            arrow
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
            }}
          >
            <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </CustomTooltip>
        </MDBox>

        {/* Body Section */}
        <MDBox pt={1} pb={3} px={2}>
          {/* Loading State */}
          {loading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress color="accent" />
            </MDBox>
          ) : error ? (
            // Error State
            <MDTypography color="error" align="center">
              {error}
            </MDTypography>
          ) : profiles.length > 0 ? (
            // Profiles Display
            <MDBox
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              gap={2}
              px={2}
              mt={2}
            >
              {profiles.map((profile, index) => (
                <Grow
                  in={true}
                  timeout={index * 50}
                  key={profile.id}
                  style={{ transformOrigin: "0 0 0" }}
                >
                  <MDBox
                    key={profile.id}
                    sx={{
                      flex: "1 1 calc(20% - 16px)", // Dynamically adjust card width
                      minWidth: "240px", // Minimum width for each card
                      maxWidth: "300px", // Prevent cards from getting too large
                    }}
                  >
                    <SocialCard
                      platform="YouTube"
                      profilePic={profile.profilePictureUrl}
                      name={profile.name}
                      count={profile.count}
                      link={profile.link}
                      verifiedStatus={profile.verifiedStatus}
                      updated_at={profile.updated_at}
                      isSelected={isSelected(profile.id)}
                      isOwn={profile.own}
                      onSelect={() =>
                        onProfileSelect(
                          profile.id,
                          profile.name,
                          profile.profilePictureUrl,
                          profile.link,
                          "YouTube")
                      }
                    />
                  </MDBox>
                </Grow>
              ))}
            </MDBox>
          ) : (
            // No Profiles Found
            <MDTypography variant="subtitle1" color="text" align="center">
              No profiles found.
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
};

// 🛡️ PropTypes Validation
YouTubeSocialCard.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      profilePictureUrl: PropTypes.string,
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      verifiedStatus: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  formatCount: PropTypes.func.isRequired,
  selectedProfiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      platform: PropTypes.string.isRequired,
    })
  ).isRequired,
  onProfileSelect: PropTypes.func.isRequired,
};

// 🛡️ Default Props
// YouTubeSocialCard.defaultProps = {
//   loading: false,
//   error: null,
// };

export default YouTubeSocialCard;
